import React, { useEffect, useState } from 'react';
import { Bell, Headphones, LayoutGrid, LineChart, Sparkles, Users } from 'lucide-react';
import { saasHeroScreens } from './saasHeroScreens';

const LOGO = '/kolanut_logo.png';
const tabIcons = [Sparkles, LineChart, Users, Headphones, Bell];

function useMobileStyles(mobile) {
  return {
    pad: mobile ? 'px-3' : 'px-3.5',
    bodyPad: mobile ? 'px-3 pt-2.5 pb-2' : 'px-3.5 pt-3 pb-2',
    label: mobile ? 'text-[10px]' : 'text-[11px]',
    value: mobile ? 'text-[15px]' : 'text-base',
    meta: mobile ? 'text-[10px]' : 'text-[11px]',
    row: mobile ? 'text-[11px]' : 'text-xs',
    section: mobile ? 'text-[10px] tracking-wide' : 'text-[11px] tracking-wide',
  };
}

function MetricStrip({ metrics, mobile, s }) {
  return (
    <div className="flex rounded-2xl bg-white ring-1 ring-black/[0.04] shadow-sm overflow-hidden shrink-0">
      {metrics.map((m, i) => (
        <div
          key={m.label}
          className={`flex-1 min-w-0 px-2.5 py-2.5 text-center ${i > 0 ? 'border-l border-gray-100' : ''}`}
        >
          <p className={`${s.meta} text-gray-400 font-medium truncate`}>{m.label}</p>
          <p className={`${s.value} font-semibold text-gray-900 tabular-nums leading-tight mt-0.5`}>
            {m.value}
          </p>
          <p
            className={`${s.meta} font-medium mt-0.5 ${
              m.positive === true ? 'text-emerald-600' : m.positive === false ? 'text-red-500' : 'text-gray-400'
            }`}
          >
            {m.change}
          </p>
        </div>
      ))}
    </div>
  );
}

function InsightCard({ screen, mobile, s }) {
  if (!screen.insight) return null;
  return (
    <div
      className={`shrink-0 rounded-2xl bg-[#f6fbf6] ring-1 ring-[#8ae690]/25 ${mobile ? 'px-3 py-2.5' : 'px-3.5 py-3'}`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className={`${s.row} text-gray-700 leading-snug flex-1`}>{screen.insight}</p>
        {screen.actionLabel && (
          <span
            className={`shrink-0 font-semibold text-[#2d4a1e] ${s.meta} px-2 py-1 rounded-lg bg-white/80 ring-1 ring-[#8ae690]/30`}
          >
            {screen.actionLabel}
          </span>
        )}
      </div>
    </div>
  );
}

function CopilotPanel({ screen, mobile, s }) {
  return (
    <div className="flex flex-1 min-h-0 flex-col gap-2.5">
      <MetricStrip metrics={screen.metrics} mobile={mobile} s={s} />
      <div className="flex flex-1 min-h-0 flex-col rounded-2xl bg-white ring-1 ring-black/[0.04] shadow-sm overflow-hidden">
        <div className={`flex items-center gap-2 border-b border-gray-50 ${mobile ? 'px-3 py-2' : 'px-3.5 py-2.5'}`}>
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#e8f9e9]">
            <Sparkles className="h-3.5 w-3.5 text-[#2d4a1e]" strokeWidth={2} />
          </span>
          <p className={`${s.row} font-semibold text-gray-900`}>Copilot</p>
          <span className="ml-auto h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
        </div>
        <div className={`flex-1 min-h-0 flex flex-col gap-2 ${mobile ? 'p-2.5' : 'p-3'}`}>
          {screen.copilot.map((msg, i) => (
            <div
              key={i}
              className={`max-w-[88%] rounded-2xl px-3 py-2 leading-snug ${s.row} ${
                msg.role === 'user'
                  ? 'ml-auto bg-gray-900 text-white'
                  : 'mr-auto bg-gray-50 text-gray-800 ring-1 ring-gray-100'
              }`}
            >
              {msg.text}
            </div>
          ))}
        </div>
        {screen.suggestions?.length ? (
          <div className={`flex gap-1.5 border-t border-gray-50 ${mobile ? 'p-2' : 'p-2.5'}`}>
            {screen.suggestions.map((chip) => (
              <span
                key={chip}
                className={`rounded-full bg-gray-100 text-gray-700 font-medium ${s.meta} px-2.5 py-1`}
              >
                {chip}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function AdsPanel({ screen, mobile, s }) {
  return (
    <div className="flex flex-1 min-h-0 flex-col gap-2.5">
      <MetricStrip metrics={screen.metrics} mobile={mobile} s={s} />
      <div className="flex-1 min-h-0 flex flex-col gap-2">
        {screen.campaigns.map((c) => (
          <div key={c.name} className="rounded-2xl bg-white p-3 ring-1 ring-black/[0.04] shadow-sm">
            <div className="flex items-start justify-between gap-2 mb-2">
              <p className={`${s.row} font-semibold text-gray-900 truncate`}>{c.name}</p>
              <p className={`${s.row} font-semibold text-red-500 tabular-nums shrink-0`}>{c.waste}</p>
            </div>
            <div className="h-1 rounded-full bg-gray-100 overflow-hidden mb-2">
              <div className="h-full rounded-full bg-red-400/90" style={{ width: `${c.bar}%` }} />
            </div>
            <div className="flex items-center justify-between">
              <span className={`${s.meta} text-gray-400`}>Risk {c.bar}%</span>
              <span className={`${s.meta} font-semibold text-[#2d4a1e]`}>{c.action} →</span>
            </div>
          </div>
        ))}
      </div>
      <InsightCard screen={screen} mobile={mobile} s={s} />
    </div>
  );
}

function OnboardPanel({ screen, mobile, s }) {
  return (
    <div className="flex flex-1 min-h-0 flex-col gap-2.5">
      <MetricStrip metrics={screen.metrics} mobile={mobile} s={s} />
      <div className="flex-1 min-h-0 rounded-2xl bg-white p-3 ring-1 ring-black/[0.04] shadow-sm flex flex-col gap-2.5">
        {screen.steps.map((step) => (
          <div key={step.label}>
            <div className="flex items-center justify-between mb-1">
              <p className={`${s.row} font-medium text-gray-800`}>{step.label}</p>
              <p className={`${s.meta} font-semibold text-gray-900 tabular-nums`}>{step.pct}%</p>
            </div>
            <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-[#8ae690]"
                style={{ width: `${step.pct}%` }}
              />
            </div>
          </div>
        ))}
      </div>
      <InsightCard screen={screen} mobile={mobile} s={s} />
    </div>
  );
}

function SupportPanel({ screen, mobile, s }) {
  const tone = {
    resolved: 'bg-emerald-50 text-emerald-700',
    open: 'bg-amber-50 text-amber-800',
    pending: 'bg-sky-50 text-sky-700',
  };

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-2.5">
      <MetricStrip metrics={screen.metrics} mobile={mobile} s={s} />
      <div className="flex-1 min-h-0 flex flex-col gap-1.5">
        {screen.tickets.map((ticket) => (
          <div
            key={ticket.subject}
            className="flex items-center gap-2.5 rounded-2xl bg-white px-3 py-2.5 ring-1 ring-black/[0.04] shadow-sm"
          >
            <div className="min-w-0 flex-1">
              <p className={`${s.row} font-medium text-gray-900 truncate`}>{ticket.subject}</p>
              <p className={`${s.meta} text-gray-400 mt-0.5`}>{ticket.time} ago</p>
            </div>
            <span className={`shrink-0 rounded-md px-2 py-0.5 font-semibold ${s.meta} ${tone[ticket.tone]}`}>
              {ticket.status}
            </span>
          </div>
        ))}
      </div>
      {screen.agentNote && (
        <p className={`shrink-0 text-center ${s.meta} text-gray-500 py-1`}>{screen.agentNote}</p>
      )}
    </div>
  );
}

function RetainPanel({ screen, mobile, s }) {
  const risk = { High: 'text-red-600 bg-red-50', Med: 'text-amber-700 bg-amber-50' };

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-2.5">
      <MetricStrip metrics={screen.metrics} mobile={mobile} s={s} />
      <div className="flex-1 min-h-0 flex flex-col gap-1.5">
        {screen.accounts.map((acc) => (
          <div
            key={acc.name}
            className="flex items-center gap-3 rounded-2xl bg-white px-3 py-2.5 ring-1 ring-black/[0.04] shadow-sm"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-gray-600">
              {acc.name.charAt(0)}
            </span>
            <div className="min-w-0 flex-1">
              <p className={`${s.row} font-semibold text-gray-900 truncate`}>{acc.name}</p>
              <p className={`${s.meta} text-gray-400`}>
                {acc.plan} · {acc.mrr}
              </p>
            </div>
            <span className={`shrink-0 rounded-md px-2 py-0.5 font-semibold ${s.meta} ${risk[acc.risk]}`}>
              {acc.risk}
            </span>
          </div>
        ))}
      </div>
      <InsightCard screen={screen} mobile={mobile} s={s} />
    </div>
  );
}

export default function SaasHeroPhone({ className = '', mobile = false }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const s = useMobileStyles(mobile);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % saasHeroScreens.length);
    }, 4500);
    return () => clearInterval(interval);
  }, []);

  const screen = saasHeroScreens[activeIndex];

  const renderBody = () => {
    switch (screen.type) {
      case 'copilot':
        return <CopilotPanel screen={screen} mobile={mobile} s={s} />;
      case 'ads':
        return <AdsPanel screen={screen} mobile={mobile} s={s} />;
      case 'onboard':
        return <OnboardPanel screen={screen} mobile={mobile} s={s} />;
      case 'support':
        return <SupportPanel screen={screen} mobile={mobile} s={s} />;
      case 'retain':
        return <RetainPanel screen={screen} mobile={mobile} s={s} />;
      default:
        return null;
    }
  };

  return (
    <div
      className={`rounded-[3rem] border-[10px] border-gray-900 bg-gray-900 shadow-[0_32px_80px_-14px_rgba(0,0,0,0.38)] overflow-hidden ring-1 ring-white/10 flex flex-col w-full min-h-0 ${className}`}
    >
      <div className="flex flex-col flex-1 min-h-0 bg-[#f8f9fb]">
        <div
          className={`flex items-center justify-between bg-[#f8f9fb] shrink-0 ${mobile ? 'px-4 py-1.5' : 'px-4 py-2'}`}
        >
          <span className={`font-semibold text-gray-900 tabular-nums ${s.meta}`}>9:41</span>
          <span className={`font-medium text-gray-500 ${s.meta}`}>100%</span>
        </div>

        <header
          className={`flex items-center bg-white shrink-0 shadow-sm shadow-black/[0.03] ${mobile ? 'gap-2.5 mx-2 mt-1 rounded-2xl px-3 py-2.5' : 'gap-3 mx-2.5 mt-1.5 rounded-2xl px-3.5 py-3'}`}
        >
          <img
            src={LOGO}
            alt="Kolanut"
            className={`rounded-xl object-contain shrink-0 ${mobile ? 'h-8 w-8' : 'h-9 w-9'}`}
          />
          <div className="flex-1 min-w-0">
            <p className={`font-semibold text-gray-900 truncate ${mobile ? 'text-[13px]' : 'text-sm'}`}>
              {screen.title}
            </p>
            <p className={`text-gray-500 truncate ${s.meta}`}>{screen.subtitle}</p>
          </div>
        </header>

        <div className={`flex flex-1 min-h-0 flex-col overflow-hidden ${s.bodyPad}`}>
          <div key={screen.id} className="flex flex-1 min-h-0 flex-col animate-[saasFade_0.35s_ease-out]">
            {renderBody()}
          </div>
        </div>

        <nav
          className={`flex items-center justify-around bg-white shrink-0 border-t border-gray-100/80 ${mobile ? 'py-2 px-1 mx-2 mb-2 rounded-2xl shadow-sm shadow-black/[0.04]' : 'py-2.5 px-2 mx-2.5 mb-2.5 rounded-2xl shadow-sm'}`}
          aria-label="App sections"
        >
          {saasHeroScreens.map((tab, i) => {
            const Icon = tabIcons[i] || LayoutGrid;
            const active = i === activeIndex;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveIndex(i)}
                className={`flex flex-col items-center gap-0.5 rounded-xl transition-colors ${mobile ? 'min-w-[2.75rem] py-1 px-1' : 'min-w-[3rem] py-1 px-1.5'} ${
                  active ? 'bg-gray-100 text-gray-900' : 'text-gray-400'
                }`}
                aria-label={tab.label}
                aria-current={active ? 'true' : undefined}
              >
                <Icon className={mobile ? 'h-[18px] w-[18px]' : 'h-5 w-5'} strokeWidth={active ? 2.25 : 1.75} />
                <span className={`font-medium ${mobile ? 'text-[9px]' : 'text-[10px]'}`}>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      <style jsx>{`
        @keyframes saasFade {
          from {
            opacity: 0;
            transform: translateY(6px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
